function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import React, { useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { RelativeDate } from 'common/types/reports/bookmark';
import { TimeUnit, TimeUnitLabelTranslatable } from 'common/widgets/date-range-picker/models/types';
import { LIMITED_ATTRIBUTION_PROJECTS } from 'common/report/constants';
import { useStagedState } from 'common/hooks/useStagedState';
import { withStyles } from 'common/util/react';
import { TooltipReact } from 'common/components/tooltip';
import { SvgIconReact } from 'common/components/svg-icon';
import { InputReact } from 'common/components/input';
import { ButtonSize, ButtonTheme } from 'common/components/button/constants';
import { InputSize, InputType, TextAlign, ValidationStatus } from 'common/components/input/types';
import { parseNumber } from 'common/util';
import { SelectReact } from 'common/components/select';
import { ButtonReact } from 'common/components/button';
import { useTranslation } from 'common/i18n/hooks';
import { DefaultMultiAttributionLookbackDays, DefaultMultiAttributionLookbackWindow, MaxMultiAttributionLookbackDays, MinMultiAttributionLookbackDays, MultiAttributionLookbackSessions, ReducedMaxMultiAttributionLookbackDays } from './constants';
import styles from './index.module.styl';
import css from './index.module.styl?inline';
interface Props {
  elref?: string;
  lookbackWindow: RelativeDate;
  projectId: number;
  onCancel?: () => void;
  onChange?: (lookbackWindow: RelativeDate) => void;
}
export const MultiAttributionLookback = withStyles((props: Props) => {
  const [stagedLookbackWindow, setStagedLookbackWindow] = useStagedState(props.lookbackWindow, props.onChange);
  const {
    t
  } = useTranslation();
  const maxLookbackDays = LIMITED_ATTRIBUTION_PROJECTS.has(props.projectId.toString()) ? ReducedMaxMultiAttributionLookbackDays : MaxMultiAttributionLookbackDays;
  const isDefaultLookbackWindow = useMemo(() => isEqual(stagedLookbackWindow, DefaultMultiAttributionLookbackWindow), [stagedLookbackWindow]);
  const lookbackUnitSections = useMemo(() => [{
    items: [TimeUnit.Day, TimeUnit.Session].map(timeUnit => {
      return {
        label: TimeUnitLabelTranslatable[timeUnit](stagedLookbackWindow.value),
        value: timeUnit,
        isSelected: timeUnit === stagedLookbackWindow.unit
      };
    })
  }], [stagedLookbackWindow.unit]);
  const invalidText = useMemo(() => {
    const {
      unit,
      value
    } = stagedLookbackWindow;
    if (unit === TimeUnit.Day) {
      if (!Number.isInteger(value)) {
        return t("query-builder.attribution.error.integer-lookback");
      }
      if (value < MinMultiAttributionLookbackDays || value > maxLookbackDays) {
        return t("query-builder.attribution.error.lookback-max", {
          count: maxLookbackDays
        });
      }
    }
    return "";
  }, [stagedLookbackWindow, maxLookbackDays]);
  const onChangeUnit = (unit: TimeUnit) => {
    setStagedLookbackWindow({
      unit,
      value: unit === TimeUnit.Session ? MultiAttributionLookbackSessions : DefaultMultiAttributionLookbackDays
    });
  };
  const onClickReset = () => setStagedLookbackWindow(DefaultMultiAttributionLookbackWindow);
  return <div elref={props.elref} className={styles.content}>
      <div className={styles.title}>
        {t("query-builder.attribution.lookback-window")}
        <div className={styles.infoContainer}>
          <SvgIconReact className={styles.infoIcon} icon='info' />
          <TooltipReact maxWidth='250px'>{t("query-builder.attribution.lookback-description")}</TooltipReact>
        </div>
      </div>
      <div className={styles.body}>
        <div className={styles.windowSection}>
          <div>
            <InputReact className={styles.valueInput} disabled={stagedLookbackWindow.unit === TimeUnit.Session} size={InputSize.Large} type={InputType.Number} min={MinMultiAttributionLookbackDays.toString()} max={maxLookbackDays.toString()} textAlign={TextAlign.Center} validationStatus={invalidText ? ValidationStatus.Invalid : ValidationStatus.Default} value={stagedLookbackWindow.value !== null ? stagedLookbackWindow.value.toString() : ""} onInput={ev => setStagedLookbackWindow(_objectSpread(_objectSpread({}, stagedLookbackWindow), {}, {
            value: parseNumber(ev.target.value)
          }))} />
            {stagedLookbackWindow.unit === TimeUnit.Session && <TooltipReact text={t("query-builder.attribution.error.session-limit")} />}
          </div>
          <SelectReact className={styles.unit} sections={lookbackUnitSections} menuWidth={0} size={ButtonSize.Large} onChange={ev => onChangeUnit(ev.detail.item.value)} />
        </div>
        {invalidText && <div className={styles.error}>{invalidText}</div>}
      </div>
      <div className={styles.footer}>
        <ButtonReact className={styles.resetButton} disabled={isDefaultLookbackWindow} theme={ButtonTheme.Tertiary} onClick={onClickReset}>
          {t("common.reset")}
        </ButtonReact>
        <div className={styles.coreButtons}>
          <ButtonReact theme={ButtonTheme.Tertiary} onClick={props.onCancel}>
            {t("common.cancel")}
          </ButtonReact>
          <ButtonReact className={styles.applyButton} disabled={!!invalidText} theme={ButtonTheme.Primary} onClick={() => {
          var _props$onChange;
          return (_props$onChange = props.onChange) === null || _props$onChange === void 0 ? void 0 : _props$onChange.call(props, stagedLookbackWindow);
        }}>
            {t("common.apply")}
          </ButtonReact>
        </div>
      </div>
    </div>;
}, css);